import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { requestsApi, commentsApi, offersApi, auditLogsApi, transportsApi, missionsApi, patientsApi, Request, AuditLog, Transport, Patient, Mission, PatientAssignment } from '../../services/api';
import UTCTime from '../../components/UTCTime/UTCTime';
import TransportTimeline from '../../components/TransportTimeline/TransportTimeline';
import PatientDetailModal from '../../components/PatientDetailModal/PatientDetailModal';
import PatientListModal from '../../components/PatientListModal/PatientListModal';
import OfferItem from '../../components/OfferItem/OfferItem';
import OfferCreationModal from '../../components/OfferCreationModal/OfferCreationModal';
import MEDEVACRouteMap from '../../components/MEDEVACRouteMap/MEDEVACRouteMap';
import SimpleOfferForm from '../../components/SimpleOfferForm/SimpleOfferForm';
import SimpleOfferItem from '../../components/SimpleOfferItem/SimpleOfferItem';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import StickyContextBar from '../../components/StickyContextBar/StickyContextBar';
import StatusProgressBar from '../../components/StatusProgressBar/StatusProgressBar';
import TabPanel from '../../components/TabPanel/TabPanel';
import useRecentItems from '../../hooks/useRecentItems';
import { Paperclip, ChevronDown, ChevronRight, FileText, FileSpreadsheet, Lightbulb, AlertTriangle, ClipboardList, ArrowLeft, Pencil, Send, XCircle, Upload, Bell, Lock, HandCoins, Play, CheckCircle2, Download, Users, Handshake, MessageSquare, Clock, Maximize2, Pin, GitBranch, ExternalLink, Copy, HeartPulse, Truck, CircleDot } from 'lucide-react';
import { getNationByName } from '../../constants/nations';
import { exportAoaToExcel } from '../../utils/excelExport';
import './RequestDetail.css';

/** Derive a human-readable role label from the user's boolean role flags using i18n. */
function getCommentRoleLabel(commentUser: {
  nation?: string;
  isAdmin?: boolean;
  isExcon?: boolean;
  isEU?: boolean;
  isPlanner?: boolean;
  isHub?: boolean;
  isNation?: boolean;
}, t: (key: string) => string): string {
  if (commentUser.isAdmin) return t('roles.admin');
  if (commentUser.isExcon) return t('roles.excon');
  if (commentUser.isEU) return t('roles.eu');
  if (commentUser.isPlanner) return t('roles.planner');
  if (commentUser.isHub) return t('roles.hub');
  if (commentUser.isNation) return `${commentUser.nation || ''} ${t('roles.nation')}`.trim();
  return commentUser.nation || '';
}

const COMMENT_AUTHOR_COLOR_COUNT = 6;

/** Deterministically assign a color index (0-5) to a user ID for comment styling */
function getCommentAuthorColorIndex(userId: string | number): number {
  if (typeof userId === 'number') return Math.abs(userId) % COMMENT_AUTHOR_COLOR_COUNT;
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % COMMENT_AUTHOR_COLOR_COUNT;
}

function RequestDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user, isReadOnlyInstance } = useAuth();
  const navigate = useNavigate();
  const { addRecentItem } = useRecentItems();

  const [request, setRequest] = useState<Request | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Comment form
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Reject dialog (EU)
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);

  // Publish dialog (EU)
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishComment, setPublishComment] = useState('');
  const [submittingPublish, setSubmittingPublish] = useState(false);

  // Close dialog
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [submittingClose, setSubmittingClose] = useState(false);

  // Withdraw dialog
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);

  // Audit logs
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);

  // Transport
  const [transport, setTransport] = useState<Transport | null>(null);
  const [loadingTransport, setLoadingTransport] = useState(false);

  // Missions
  const [missions, setMissions] = useState<Mission[]>([]);
  const [_loadingMissions, setLoadingMissions] = useState(false);
  const [showMissions, setShowMissions] = useState(true);
  const [expandedMissions, setExpandedMissions] = useState<Set<string>>(new Set());

  // Patient Assignments (treatment/transport status for requesting nation)
  const [patientAssignments, setPatientAssignments] = useState<Map<string, PatientAssignment>>(new Map());

  // Patient Details Modal
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showPatientModal, setShowPatientModal] = useState(false);

  // Patient List Modal
  const [showPatientListModal, setShowPatientListModal] = useState(false);

  // Edit Offer Modal
  const [editingOffer, setEditingOffer] = useState<any>(null);
  const [showEditOfferModal, setShowEditOfferModal] = useState(false);

  // Confirm Dialogs
  const [showManageOffersConfirm, setShowManageOffersConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [showMakeOfferConfirm, setShowMakeOfferConfirm] = useState(false);
  const [makeOfferType, setMakeOfferType] = useState<'medevac' | 'text'>('medevac');

  // EU Direct-Assign Transport
  const [showDirectAssign, setShowDirectAssign] = useState(false);
  const [directAssignAsset, setDirectAssignAsset] = useState('');
  const [directAssignDate, setDirectAssignDate] = useState('');
  const [directAssignNotes, setDirectAssignNotes] = useState('');
  const [directAssignSubmitting, setDirectAssignSubmitting] = useState(false);

  // Simple Text Offer for Personnel/Material/Other
  const [showTextOfferModal, setShowTextOfferModal] = useState(false);
  const [offerText, setOfferText] = useState('');
  const [_submittingOffer, setSubmittingOffer] = useState(false);
  const [editingTextOffer, setEditingTextOffer] = useState<any>(null);

  // Create Offer Modal (for new offers)
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [selectedPatientIdsForOffer, setSelectedPatientIdsForOffer] = useState<string[]>([]);

  // Consolidated request creation & unmatched patient actions
  const [creatingFollowup, setCreatingFollowup] = useState(false);
  const [showFollowupConfirm, setShowFollowupConfirm] = useState(false);
  const [showUnmatchedConfirm, setShowUnmatchedConfirm] = useState(false);
  const [unmatchedAction, setUnmatchedAction] = useState<'discard' | 'export-discard' | 'followup'>('discard');

  // Excel import into consolidated draft
  const [importingExcel, setImportingExcel] = useState(false);
  const excelInputRef = React.useRef<HTMLInputElement>(null);
  const [excelPreviewFile, setExcelPreviewFile] = useState<File | null>(null);
  const [showExcelPreview, setShowExcelPreview] = useState(false);
  const [previewPatients, setPreviewPatients] = useState<any[]>([]);
  const [previewErrors, setPreviewErrors] = useState<Array<{ row: number | string; error?: string; message?: string; level?: string }>>([]);
  const [previewSummary, setPreviewSummary] = useState<any>(null);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [showPreviewSkipped, setShowPreviewSkipped] = useState(false);
  const [showPreviewWarnings, setShowPreviewWarnings] = useState(false);

  // Tab navigation
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ['overview', 'patients', 'offers', 'comments', 'history'] as const;
  type TabId = typeof validTabs[number];
  const tabParam = searchParams.get('tab') as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    tabParam && validTabs.includes(tabParam) ? tabParam : 'overview'
  );

  // Patient tab filters
  const [patientSearch, setPatientSearch] = useState('');
  const [patientPriorityFilter, setPatientPriorityFilter] = useState('');
  const [patientTransportFilter, setPatientTransportFilter] = useState('');
  const [patientSortBy, setPatientSortBy] = useState('');

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as TabId);
    setSearchParams(tabId === 'overview' ? {} : { tab: tabId }, { replace: true });
  };

  // Sync tab state when URL search params change (e.g., browser back/forward)
  useEffect(() => {
    const urlTab = searchParams.get('tab') as TabId | null;
    const resolved = urlTab && validTabs.includes(urlTab) ? urlTab : 'overview';
    if (resolved !== activeTab) {
      setActiveTab(resolved);
    }
  }, [searchParams]);

  useEffect(() => {
    if (id) {
      loadRequest();
      loadAuditLogs();
      loadTransport();
      loadMissions();
      loadPatientAssignments();
    }
  }, [id]);

  const loadRequest = async () => {
    try {
      setLoading(true);
      const response = await requestsApi.getById(id!);
      setRequest(response.data);
      addRecentItem({
        id: response.data.id,
        type: 'request',
        label: response.data.requestId || response.data.title,
        path: `/requests/${response.data.id}`
      });
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      setLoadingAuditLogs(true);
      const response = await auditLogsApi.getForResource('Request', id!);
      setAuditLogs(response.data.logs);
    } catch (err: any) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  const loadTransport = async () => {
    // Only load transport for ACCEPTED, PLANNED, IN_TRANSIT, COMPLETED status
    try {
      setLoadingTransport(true);
      const response = await transportsApi.getByRequestId(id!);
      setTransport(response.data);
    } catch (err: any) {
      // 404 is expected if no transport exists yet
      if (err.response?.status !== 404) {
        console.error('Failed to load transport:', err);
      }
      setTransport(null);
    } finally {
      setLoadingTransport(false);
    }
  };

  const loadMissions = async () => {
    try {
      setLoadingMissions(true);
      const response = await missionsApi.getByRequestId(id!);
      setMissions(response.data);
    } catch (err: any) {
      // Silent - missions may not exist
      setMissions([]);
    } finally {
      setLoadingMissions(false);
    }
  };

  const loadPatientAssignments = async () => {
    try {
      const response = await requestsApi.getPatientAssignments(id!);
      const assignMap = new Map<string, PatientAssignment>();
      response.data.forEach(a => assignMap.set(a.patientId, a));
      setPatientAssignments(assignMap);
    } catch {
      // Silent - assignments may not exist or user may not have access
      setPatientAssignments(new Map());
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      if (newStatus === 'PUBLISHED' && request) {
        await requestsApi.publish(request.id);
      } else if (request) {
        await requestsApi.updateStatus(request.id, newStatus);
      }
      setSuccess(`Status updated to ${newStatus}`);
      await loadRequest();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToUpdate'));
    }
  };

  const handleSubmitForReview = async () => {
    try {
      if (request) {
        await requestsApi.submit(request.id);
        setSuccess(t('common.success'));
        await loadRequest();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    }
  };

  const handleSendReminder = async () => {
    try {
      if (request) {
        const response = await requestsApi.sendReminder(request.id);
        setSuccess(`Reminder sent to ${response.data.recipientCount} nation(s)`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    }
  };

  const handleDeleteRequest = async () => {
    setShowWithdrawDialog(false);
    try {
      if (request) {
        await requestsApi.deleteRequest(request.id);
        setSuccess(t('common.deleted'));
        setTimeout(() => navigate('/requests'), 1500);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToDelete'));
    }
  };

  const handleRevertToDraft = async () => {
    setShowWithdrawDialog(false);
    try {
      if (request) {
        await requestsApi.revertToDraft(request.id);
        setSuccess(t('common.success'));
        await loadRequest();
        await loadAuditLogs();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToUpdate'));
    }
  };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmittingPublish(true);
      if (request) {
        // First publish the request
        await handleStatusChange('PUBLISHED');

        // Then add the review comment if provided
        if (publishComment.trim()) {
          await commentsApi.create({
            requestId: request.id,
            content: `**EU Review Comment:**\n\n${publishComment}`
          });
        }

        setSuccess(t('common.success'));
        setShowPublishDialog(false);
        setPublishComment('');
        await loadRequest();
        await loadAuditLogs();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToUpdate'));
    } finally {
      setSubmittingPublish(false);
    }
  };

  const handleReject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectMessage.trim()) {
      setError(t('requests.fieldRequired'));
      return;
    }

    try {
      setSubmittingReject(true);
      if (request) {
        await requestsApi.reject(request.id, rejectMessage);
        setSuccess(t('common.success'));
        setShowRejectDialog(false);
        setRejectMessage('');
        await loadRequest();
        await loadAuditLogs();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToUpdate'));
    } finally {
      setSubmittingReject(false);
    }
  };

  const handleClose = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmittingClose(true);
      if (request) {
        await handleStatusChange('CLOSED');
        setSuccess(t('common.success'));
        setShowCloseDialog(false);
        await loadRequest();
        await loadAuditLogs();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToUpdate'));
    } finally {
      setSubmittingClose(false);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !request) return;

    try {
      setSubmittingComment(true);
      await commentsApi.create({
        requestId: request.id,
        content: commentText
      });
      setCommentText('');
      setSuccess(t('common.success'));
      await loadRequest();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAcceptOffer = async (offerId: string) => {
    // Show warning if EU is accepting (not the creator)
    const isEuAccepting = (user?.isEU || user?.isAdmin) && request?.creatorId !== user?.id;
    const isSimpleTextOffer = ['PERSONNEL', 'MATERIAL', 'OTHER'].includes(request?.category || '');

    let confirmMessage = isSimpleTextOffer
      ? t('offers.confirmAccept')
      : t('offers.confirmAccept');

    if (isEuAccepting) {
      confirmMessage = 'WARNING: You are accepting an offer on behalf of another nation.\n\n' +
        'Normally, the requesting nation should accept offers themselves.\n' +
        'Only proceed if you have explicit authorization.\n\n' +
        'Do you want to continue?';
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await offersApi.accept(offerId);
      const successMessage = t('common.success');
      setSuccess(successMessage);
      await loadRequest();
      await loadAuditLogs();
      await loadPatientAssignments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    }
  };

  const handleWithdrawOffer = async (offerId: string) => {
    try {
      await offersApi.delete(offerId);
      setSuccess(t('common.success'));
      await loadRequest();
      await loadAuditLogs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    }
  };

  const handleEditOffer = async (offerId: string) => {
    // Navigate directly to fullpage offer creation/edit
    // TODO: Implement edit mode in OfferCreate fullpage (pass offerId as query param or route param)
    navigate(`/requests/${request?.id}/offers/create?edit=${offerId}`);
  };

  const handleEditOfferSuccess = async () => {
    setShowEditOfferModal(false);
    setEditingOffer(null);
    setSuccess(t('common.updated'));
    await loadRequest();
    await loadAuditLogs();
    setTimeout(() => setSuccess(''), 3000);
  };

  const _handlePatientSelectionForEdit = (patientIds: string[]) => {
    // After patient selection, open the offer creation modal with selected patients
    setShowPatientListModal(false);
    setShowEditOfferModal(true);
    // Update the editing offer with new patient IDs
    if (editingOffer) {
      // We need to update the patient IDs in the editing offer
      // The OfferCreationModal will receive these
      setEditingOffer({
        ...editingOffer,
        selectedPatientIds: patientIds
      });
    }
  };

  // Text Offer Handlers for Personnel/Material/Other
  const handleCreateTextOffer = () => {
    setOfferText('');
    setEditingTextOffer(null);
    setShowTextOfferModal(true);
  };

  const handleEditTextOffer = (offer: any) => {
    setOfferText(offer.description || '');
    setEditingTextOffer(offer);
    setShowTextOfferModal(true);
  };

  const _handleSubmitTextOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!offerText.trim()) return;

    setSubmittingOffer(true);
    setError('');

    try {
      if (editingTextOffer) {
        // Update existing offer
        await offersApi.update(editingTextOffer.id, { description: offerText });
        setSuccess(t('common.updated'));
      } else {
        // Create new offer
        await offersApi.create({
          requestId: request!.id,
          description: offerText
        });
        setSuccess(t('common.success'));
      }

      setShowTextOfferModal(false);
      setOfferText('');
      setEditingTextOffer(null);
      await loadRequest();
      await loadAuditLogs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    } finally {
      setSubmittingOffer(false);
    }
  };

  const handleWithdrawTextOffer = async (offerId: string) => {
    if (!window.confirm('Withdraw this offer? This action cannot be undone.')) {
      return;
    }

    try {
      await offersApi.withdraw(offerId);
      setSuccess(t('common.success'));
      await loadRequest();
      await loadAuditLogs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    }
  };

  const _handlePatientSelectionForNewOffer = (patientIds: string[]) => {
    // After patient selection, open the offer creation modal with selected patients
    setShowPatientListModal(false);
    setSelectedPatientIdsForOffer(patientIds);
    setShowCreateOfferModal(true);
  };

  const handleCreateOfferSuccess = async () => {
    setShowCreateOfferModal(false);
    setSelectedPatientIdsForOffer([]);
    setSuccess(t('common.created'));
    await loadRequest();
    await loadAuditLogs();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleStartTransport = async () => {
    if (!transport) return;

    if (!window.confirm('Start this transport? This will change the status to IN_TRANSIT.')) {
      return;
    }

    try {
      await transportsApi.start(transport.id);
      setSuccess(t('common.success'));
      await loadRequest();
      await loadTransport();
      await loadAuditLogs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToUpdate'));
    }
  };

  const handleCompleteTransport = async () => {
    if (!transport) return;

    if (!window.confirm('Confirm transport arrival? This will mark the request as COMPLETED.')) {
      return;
    }

    try {
      await transportsApi.complete(transport.id);
      setSuccess(t('common.success'));
      await loadRequest();
      await loadTransport();
      await loadAuditLogs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToUpdate'));
    }
  };

  // Create consolidated request for unmatched patients
  const handleCreateFollowup = async (unmatchedPatientIds: string[]) => {
    if (!request || unmatchedPatientIds.length === 0) return;
    setCreatingFollowup(true);
    try {
      const response = await requestsApi.createFollowup(request.id, unmatchedPatientIds);
      setSuccess(`Consolidated request ${response.data.requestId} created with ${response.data.patientCount} patient(s)`);
      setShowFollowupConfirm(false);
      // Reload to show the new consolidated link
      await loadRequest();
      setTimeout(() => setSuccess(''), 5000);
      // Navigate to the new consolidated request
      navigate(`/requests/${response.data.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToSave'));
    } finally {
      setCreatingFollowup(false);
    }
  };

  // Import patients from Excel — Step 1: Preview
  const handleExcelImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !request) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/)) {
      setError('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10 MB');
      return;
    }

    setImportingExcel(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('patientExcel', file);
      const response = await patientsApi.previewExcel(formData);
      setExcelPreviewFile(file);
      setPreviewPatients(response.data.patients);
      setPreviewErrors(response.data.errors);
      setPreviewSummary(response.data.summary);
      setExcludedRows(new Set());
      setShowPreviewSkipped(false);
      setShowPreviewWarnings(false);
      setShowExcelPreview(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to parse Excel file');
    } finally {
      setImportingExcel(false);
      if (excelInputRef.current) excelInputRef.current.value = '';
    }
  };

  // Import patients from Excel — Step 2: Confirm import
  const handleConfirmExcelImport = async () => {
    if (!request || !excelPreviewFile) return;
    setImportingExcel(true);
    setError('');
    try {
      const response = await requestsApi.importPatientsExcel(
        request.id,
        excelPreviewFile,
        excludedRows.size > 0 ? Array.from(excludedRows) : undefined
      );
      const { imported, failed, warnings } = response.data;
      let msg = `${imported} patient(s) imported successfully`;
      if (warnings > 0) msg += ` (${warnings} warning${warnings !== 1 ? 's' : ''})`;
      if (failed > 0) msg += ` — ${failed} row(s) skipped`;
      setSuccess(msg);
      setShowExcelPreview(false);
      setExcelPreviewFile(null);
      setPreviewPatients([]);
      setPreviewErrors([]);
      setPreviewSummary(null);
      setExcludedRows(new Set());
      await loadRequest();
      setTimeout(() => setSuccess(''), 8000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import patients from Excel');
    } finally {
      setImportingExcel(false);
    }
  };

  const recalcPreviewSummary = (patients: any[], excluded: Set<number>) => {
    const active = patients.filter((_: any, i: number) => !excluded.has(i));
    return {
      total: active.length,
      civil: active.filter((p: any) => p.patientStatus === 'CIVIL').length,
      military: active.filter((p: any) => p.patientStatus === 'MILITARY').length,
      p1: active.filter((p: any) => p.medicalPriority === 'P1').length,
      p2: active.filter((p: any) => p.medicalPriority === 'P2').length,
      p3: active.filter((p: any) => p.medicalPriority === 'P3').length,
      seated: active.filter((p: any) => p.medicalTransport === 'SEATED').length,
      lying: active.filter((p: any) => p.medicalTransport === 'LYING').length,
      withCompanions: active.filter((p: any) => (p.patientCompanionsCount || 0) > 0).length,
    };
  };

  const handleExcludePatient = (idx: number) => {
    const newExcluded = new Set(excludedRows);
    if (newExcluded.has(idx)) newExcluded.delete(idx);
    else newExcluded.add(idx);
    setExcludedRows(newExcluded);
    setPreviewSummary(recalcPreviewSummary(previewPatients, newExcluded));
  };

  const getStatusBadgeClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      DRAFT: 'badge-draft',
      UNDER_REVIEW: 'badge-review',
      PUBLISHED: 'badge-published',
      UNDER_SELECTION: 'badge-selection',
      ACCEPTED: 'badge-accepted',
      PLANNED: 'badge-planned',
      CLOSED: 'badge-closed',
      IN_TRANSIT: 'badge-transit',
      COMPLETED: 'badge-completed',
      WITHDRAWN: 'badge-closed'
    };
    return statusMap[status] || 'badge-draft';
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      DRAFT: t('status.draft'),
      UNDER_REVIEW: t('status.underReview'),
      PUBLISHED: t('status.published'),
      UNDER_SELECTION: t('status.underSelection'),
      ACCEPTED: t('status.accepted'),
      PLANNED: t('status.planned'),
      CLOSED: t('status.closed'),
      IN_TRANSIT: t('status.inTransit'),
      COMPLETED: t('status.completed'),
      WITHDRAWN: t('status.withdrawn')
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      PERSONNEL: t('categories.personnel'),
      MATERIAL: t('categories.equipment'),
      MEDEVAC: t('categories.medevac'),
      TRANSPORT: 'Transport',
      OTHER: t('categories.other'),
      NEWS: t('categories.news')
    };
    return labels[category] || category;
  };

  const getCategoryBadgeClass = (category: string) => {
    const classes: { [key: string]: string } = {
      MEDEVAC: 'badge-category-medevac',
      PERSONNEL: 'badge-category-personnel',
      MATERIAL: 'badge-category-equipment',
      TRANSPORT: 'badge-category-transport',
      OTHER: 'badge-category-other',
      NEWS: 'badge-category-news'
    };
    return classes[category] || 'badge-category-other';
  };

  // Check if current user belongs to the requesting/origin nation
  const isRequestingNation = !!(request?.creator?.nation && user?.nation && request.creator.nation === user.nation);
  const canSeeAssignments = isRequestingNation || user?.isEU || user?.isAdmin || user?.isExcon;
  const hasAcceptedOffers = request?.offers?.some((o: any) => o.status === 'ACCEPTED');

  const canSubmitForReview = request?.status === 'DRAFT' && request?.creatorId === user?.id;
  const canWithdraw = ['DRAFT', 'UNDER_REVIEW', 'PUBLISHED', 'UNDER_SELECTION'].includes(request?.status || '') && request?.creatorId === user?.id && user?.isNation;
  const canReject = request?.status === 'UNDER_REVIEW' && (user?.isEU || user?.isAdmin);
  const canPublish = request?.status === 'UNDER_REVIEW' && (user?.isEU || user?.isAdmin);
  const canComment = request?.status === 'PUBLISHED' || request?.status === 'UNDER_SELECTION' || request?.status === 'CLOSED';
  const canAcceptOffer = request?.creatorId === user?.id || user?.isEU || user?.isAdmin;
  // Nation users can complete if they are creator OR if they made an offer
  const hasOffer = request?.offers?.some(offer => offer.user?.id === user?.id);
  const offerCount = request?.offers?.filter((offer: any) => offer.user?.id === user?.id).length || 0;
  const canChangeStatus = request?.creatorId === user?.id || user?.isEU || user?.isAdmin || user?.isExcon;

  // For simple text offers (Personnel/Material/Other)
  const canMakeTextOffer =
    request?.status === 'PUBLISHED' &&
    ['PERSONNEL', 'MATERIAL', 'OTHER'].includes(request?.category || '') &&
    request?.creatorId !== user?.id &&
    !hasOffer &&
    user?.isNation &&
    !user?.isExcon;

  // Timeline helpers
  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/REQUEST /g, '').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  };

  const getNationFlag = (nation: string) => {
    const nationObj = getNationByName(nation);
    return nationObj?.flagImage || '/flags/eu.png';
  };

  /** Derive a role label from audit log user roles */
  const getTimelineRoleLabel = (log: AuditLog): string | null => {
    if (!log.userRoles) return null;
    if (log.userRoles.isAdmin) return 'System Admin';
    if (log.userRoles.isExcon) return 'Exercise Controller';
    if (log.userRoles.isEU) return 'EU Coordinator';
    if (log.userRoles.isPlanner) return 'Planner';
    if (log.userRoles.isHub) return 'Hub Manager';
    if (log.userRoles.isNation) return `${log.userNation || ''} National Coordinator`.trim();
    return null;
  };

  const tabs = useMemo(() => [
    { id: 'overview', label: t('common.overview'), icon: <FileText size={16} /> },
    { id: 'patients', label: t('requests.patients'), icon: <Users size={16} />, badge: request?.patients?.length || 0 },
    { id: 'offers', label: t('requests.offers'), icon: <Handshake size={16} />, badge: request?.offers?.length || 0 },
    { id: 'comments', label: t('common.comments'), icon: <MessageSquare size={16} />, badge: request?.comments?.length || 0 },
    { id: 'history', label: t('requests.history'), icon: <Clock size={16} />, badge: auditLogs.length },
  ], [request?.patients?.length, request?.offers?.length, request?.comments?.length, auditLogs.length]);

  const filteredPatients = useMemo(() => {
    if (!request?.patients) return [];
    let filtered = [...request.patients];

    // Search by initials or diagnosis
    if (patientSearch.trim()) {
      const search = patientSearch.toLowerCase();
      filtered = filtered.filter((p: any) =>
        (p.patientInitials || '').toLowerCase().includes(search) ||
        (p.medicalPrimaryConditionDiagnosis || '').toLowerCase().includes(search) ||
        (p.patientId || '').toLowerCase().includes(search)
      );
    }

    // Filter by priority
    if (patientPriorityFilter) {
      filtered = filtered.filter((p: any) => p.medicalPriority === patientPriorityFilter);
    }

    // Filter by transport type
    if (patientTransportFilter) {
      filtered = filtered.filter((p: any) => p.medicalTransport === patientTransportFilter);
    }

    // Sort
    if (patientSortBy) {
      filtered.sort((a: any, b: any) => {
        switch (patientSortBy) {
          case 'name':
            return (a.patientInitials || '').localeCompare(b.patientInitials || '');
          case 'priority': {
            const order: Record<string, number> = { P1: 1, P2: 2, P3: 3, TBA: 4 };
            return (order[a.medicalPriority] || 99) - (order[b.medicalPriority] || 99);
          }
          case 'age':
            return (a.patientAge ?? 999) - (b.patientAge ?? 999);
          case 'gender':
            return (a.patientGender || 'ZZZ').localeCompare(b.patientGender || 'ZZZ');
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [request?.patients, patientSearch, patientPriorityFilter, patientTransportFilter, patientSortBy]);

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (!request) {
    return (
      <div className="error-page">
        <h2>{t('common.pageNotFound')}</h2>
        <button onClick={() => navigate('/requests')} className="btn btn-primary">
          <ArrowLeft size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          {t('common.goBack')}
        </button>
      </div>
    );
  }

  const _handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowPatientModal(true);
  };

  return (
    <div className="request-detail">
      <StickyContextBar
        itemId={request.requestId || 'Request'}
        status={request.status}
        statusColor={
          ['COMPLETED'].includes(request.status) ? 'success' :
          ['IN_TRANSIT', 'PLANNED'].includes(request.status) ? 'info' :
          ['PUBLISHED', 'UNDER_SELECTION', 'ACCEPTED'].includes(request.status) ? 'primary' :
          ['UNDER_REVIEW'].includes(request.status) ? 'warning' :
          ['CLOSED', 'WITHDRAWN'].includes(request.status) ? 'danger' :
          'neutral'
        }
        backPath="/requests"
      />
      <StatusProgressBar status={request.status as any} />

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Action Buttons — always visible below status bar */}
      <div className="request-actions">
          {!isReadOnlyInstance && canSubmitForReview && (
            <>
              <button onClick={() => navigate(`/requests/${request.id}/edit`)} className="btn btn-secondary btn-sm">
                <Pencil size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {t('requests.editRequest')}
              </button>
              {request.category === 'MEDEVAC' && (
                <>
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleExcelImport}
                  />
                  <button
                    className="btn btn-import"
                    onClick={() => excelInputRef.current?.click()}
                    disabled={importingExcel}
                  >
                    <Upload size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    {importingExcel ? 'Importing...' : 'Import Patients (Excel)'}
                  </button>
                </>
              )}
              <button onClick={handleSubmitForReview} className="btn btn-primary btn-sm">
                <Send size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {t('requests.submitForReview')}
              </button>
            </>
          )}
          {!isReadOnlyInstance && canWithdraw && (
            <button onClick={() => setShowWithdrawDialog(true)} className="btn btn-danger btn-sm">
              <XCircle size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {t('requests.withdrawRequest')}
            </button>
          )}
          {!isReadOnlyInstance && canReject && (
            <button onClick={() => setShowRejectDialog(true)} className="btn btn-warning btn-sm">
              <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Reject Request (Return for Review)
            </button>
          )}
          {!isReadOnlyInstance && canPublish && (
            <button onClick={() => setShowPublishDialog(true)} className="btn btn-success btn-sm">
              <Upload size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {t('requests.publish')}
            </button>
          )}
          {!isReadOnlyInstance && (user?.isEU || user?.isAdmin) && request.status === 'PUBLISHED' && (
            <button onClick={() => setShowReminderConfirm(true)} className="btn btn-warning btn-sm">
              <Bell size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Send Reminder to Nations
            </button>
          )}
          {!isReadOnlyInstance && canChangeStatus && request.status === 'PUBLISHED' && (
            <button onClick={() => setShowCloseDialog(true)} className="btn btn-secondary btn-sm">
              <Lock size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {t('requests.closeRequest')}
            </button>
          )}
          {request.category === 'MEDEVAC' && request.patients && request.patients.length > 0 && (
            <button
              onClick={() => setShowPatientListModal(true)}
              className="btn btn-outline btn-sm"
            >
              <Maximize2 size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              View Patient List
            </button>
          )}
          {!isReadOnlyInstance && request.category === 'MEDEVAC' && request.status === 'PUBLISHED' && request.creatorId !== user?.id && !user?.isExcon && (
            <button
              onClick={() => { setMakeOfferType('medevac'); setShowMakeOfferConfirm(true); }}
              className="btn btn-success btn-sm"
            >
              {offerCount > 0 ? 'Make another Offer' : 'Make an Offer'}
            </button>
          )}
          {!isReadOnlyInstance && request.category === 'TRANSPORT' && (user?.isEU || user?.isAdmin) && (request.status === 'UNDER_REVIEW' || request.status === 'PUBLISHED') && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowDirectAssign(true)}
            >
              Assign Transport Directly
            </button>
          )}
          {(request.category === 'MEDEVAC' || request.category === 'TRANSPORT') && request.creatorId === user?.id && request.offers && request.offers.length > 0 && (request.status === 'PUBLISHED' || request.status === 'UNDER_SELECTION') && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (request.status === 'PUBLISHED') {
                  setShowManageOffersConfirm(true);
                } else {
                  navigate(`/requests/${request.id}/manage-offers`);
                }
              }}
            >
              Manage Offers
            </button>
          )}
          {!isReadOnlyInstance && request.status === 'UNDER_SELECTION' && request.creatorId === user?.id && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setShowReopenConfirm(true)}
            >
              Reopen for Offers
            </button>
          )}
          {!isReadOnlyInstance && canMakeTextOffer && (
            <button onClick={() => { setMakeOfferType('text'); setShowMakeOfferConfirm(true); }} className="btn btn-success btn-sm">
              <HandCoins size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Make an Offer
            </button>
          )}
          {/* Transport planning/booking for ACCEPTED/PLANNED requests */}
          {!isReadOnlyInstance && ['ACCEPTED', 'PLANNED'].includes(request.status) && (user?.isPlanner || user?.isAdmin || user?.isExcon) && !transport && (
            <>
              <button
                onClick={() => navigate('/transport-planning')}
                className="btn btn-primary btn-sm"
              >
                Plan Transport
              </button>
              <button
                onClick={() => navigate(`/transport-booking/${request.id}`)}
                className="btn btn-secondary btn-sm"
              >
                Book on Schedule
              </button>
            </>
          )}
      </div>

      <TabPanel tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange}>

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === 'overview' && (
      <>

      <div className="card request-info">
        <div className="info-header">
          <div>
            <div className="request-id">{request.requestId}</div>
            <h1>{request.title}</h1>
          </div>
          <span className={`badge ${getStatusBadgeClass(request.status)}`}>
            {getStatusLabel(request.status)}
          </span>
        </div>

        <div className="info-content-wrapper">
          <div className="info-main">
            <div className="info-grid">
          <div className="info-item">
            <label>{t('requests.fromNation')}</label>
            <div className="value">
              {(() => {
                const nation = getNationByName(request.creator.nation);
                return nation ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <img src={nation.flagImage} alt={nation.name} style={{ width: '24px', height: '16px', objectFit: 'cover', borderRadius: '2px', border: '1px solid var(--border-color)' }} />
                    {request.creator.nation}
                  </span>
                ) : request.creator.nation;
              })()}
            </div>
          </div>
          <div className="info-item">
            <label>{t('requests.category')}</label>
            <div className="value"><span className={`category-badge ${getCategoryBadgeClass(request.category)}`}>{getCategoryLabel(request.category)}</span></div>
          </div>
          {request.parentRequest && (
            <div className="info-item">
              <label><GitBranch size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Consolidated from</label>
              <div className="value">
                <Link to={`/requests/${request.parentRequest.id}`} className="related-request-link">
                  {request.parentRequest.requestId}
                </Link>
                <span className="related-request-title">{request.parentRequest.title}</span>
              </div>
            </div>
          )}
          {request.followupRequests && request.followupRequests.length > 0 && (
            <div className="info-item full-width">
              <label><GitBranch size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Consolidated Requests</label>
              <div className="value">
                <div className="followup-requests-list">
                  {request.followupRequests.map((fr) => {
                    const canView = user?.isEU || user?.isAdmin || user?.isExcon || request.creatorId === user?.id || ['PUBLISHED', 'UNDER_SELECTION', 'ACCEPTED', 'PLANNED', 'IN_TRANSIT', 'COMPLETED'].includes(fr.status);
                    const content = (
                      <>
                        <div className="followup-row-main">
                          <span className="followup-request-id">{fr.requestId}</span>
                          <span className={`badge badge-sm ${getStatusBadgeClass(fr.status)}`}>{getStatusLabel(fr.status)}</span>
                        </div>
                        <div className="followup-row-meta">
                          <span className="followup-patient-count">{fr._count.patients} patient{fr._count.patients !== 1 ? 's' : ''}</span>
                          <span className="followup-reason">{fr.category === 'TRANSPORT' ? t('requests.missingTransport', 'Missing transport') : t('requests.noOffersReceived', 'No offers')}</span>
                        </div>
                      </>
                    );
                    return canView ? (
                      <Link key={fr.id} to={`/requests/${fr.id}`} className="followup-request-chip">
                        <ExternalLink size={12} />
                        {content}
                      </Link>
                    ) : (
                      <div key={fr.id} className="followup-request-chip followup-no-link">
                        {content}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className="info-item">
            <label>{t('requests.urgency')}</label>
            <span className={`badge badge-${request.urgency.toLowerCase()}`}>
              {request.urgency}
            </span>
          </div>
          <div className="info-item">
            <label>{t('requests.created')}</label>
            <div className="value">
              <UTCTime date={request.createdAt} />
            </div>
          </div>
          {request.answerBy && (
            <div className="info-item">
              <label>Answer By</label>
              <div className="value deadline">
                <UTCTime date={request.answerBy} />
              </div>
            </div>
          )}
          {request.publishedAt && (
            <div className="info-item">
              <label>Published</label>
              <div className="value">
                <UTCTime date={request.publishedAt} />
              </div>
            </div>
          )}
        </div>

        {/* Description - moved right after info-header */}
        <div className="info-section">
          <label>{t('requests.description')}</label>
          <div className="description">{request.description}</div>
        </div>

        {/* MEDEVAC-specific Information */}
        {request.category === 'MEDEVAC' && (
          <div className="info-section">
            <h3>MEDEVAC Details</h3>
            <div className="medevac-details-grid">
              <div className="info-item">
                <label>UCPM TRANSIT HUB</label>
                <span>{request.exitPoint ? request.exitPoint.name : '-'}</span>
              </div>
              <div className="info-item">
                <label>Ready at UCPM TRANSIT HUB</label>
                <span>{request.readyAtHub ? <UTCTime date={request.readyAtHub} /> : '-'}</span>
              </div>
              <div className="info-item full-width">
                <label>Remarks</label>
                <span>{request.medevacRemarks || '-'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Attachments Section for non-MEDEVAC requests */}
        {request.category !== 'MEDEVAC' && request.attachments && (Array.isArray(request.attachments) && request.attachments.length > 0) && (
          <div className="info-section">
            <h3>Attachments ({request.attachments.length})</h3>
            <div className="attachments-list">
              {request.attachments.map((attachment: any, index: number) => (
                <div key={index} className="attachment-item">
                  <span className="attachment-icon"><Paperclip size={16} /></span>
                  <a
                    href={`${process.env.REACT_APP_API_URL || ''}${attachment.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-link"
                  >
                    {attachment.originalName}
                  </a>
                  <span className="attachment-size">
                    ({(attachment.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Patient Statistics and Coverage */}
        {request.category === 'MEDEVAC' && request.patients && request.patients.length > 0 && (() => {
          // Calculate statistics
          const stats = {
            total: request.patients.length,
            byStatus: { CIVIL: 0, MILITARY: 0, TBA: 0 } as Record<string, number>,
            byPriority: { P1: 0, P2: 0, P3: 0, TBA: 0 } as Record<string, number>,
            byTransport: { SEATED: 0, LYING: 0, TBA: 0 } as Record<string, number>,
            byDependency: { LOW: 0, MEDIUM: 0, HIGH: 0, TBA: 0 } as Record<string, number>,
            companions: 0
          };

          request.patients.forEach((patient: any) => {
            if (patient.patientStatus && stats.byStatus[patient.patientStatus] !== undefined) stats.byStatus[patient.patientStatus]++;
            if (patient.medicalPriority && stats.byPriority[patient.medicalPriority] !== undefined) stats.byPriority[patient.medicalPriority]++;
            if (patient.medicalTransport && stats.byTransport[patient.medicalTransport] !== undefined) stats.byTransport[patient.medicalTransport]++;
            if (patient.medicalDependency && stats.byDependency[patient.medicalDependency] !== undefined) stats.byDependency[patient.medicalDependency]++;
            if (patient.patientCompanionsCount) stats.companions += patient.patientCompanionsCount;
          });

          // Track patients by actual assignment (missions) and pending offer coverage
          const patientsWithMission = new Set<string>();
          const patientsWithPendingOffer = new Set<string>();
          const allPatientIds = new Set(request.patients.map((p: any) => p.id));

          // Patients in missions = actually assigned & accepted
          if (missions && missions.length > 0) {
            missions.forEach(m => {
              if (m.parentMissionId) return;
              if (m.patients) {
                m.patients.forEach(mp => patientsWithMission.add(mp.patientId));
              }
            });
          }

          // Patients with pending offers (not yet assigned)
          if (request.offers && request.offers.length > 0) {
            request.offers.forEach((offer: any) => {
              if (offer.status !== 'PENDING') return;
              if (offer.offerPatients && offer.offerPatients.length > 0) {
                offer.offerPatients.forEach((op: any) => {
                  if (!patientsWithMission.has(op.patient.id)) {
                    patientsWithPendingOffer.add(op.patient.id);
                  }
                });
              }
            });
          }

          const assignedCount = patientsWithMission.size;
          const pendingOnlyCount = patientsWithPendingOffer.size;
          const noOffersCount = Array.from(allPatientIds).filter(id => !patientsWithMission.has(id) && !patientsWithPendingOffer.has(id)).length;
          const coveredCount = assignedCount + pendingOnlyCount;
          const percentage = stats.total > 0 ? Math.round((coveredCount / stats.total) * 100) : 0;

          // Unmatched patient IDs (no offers, no missions)
          const unmatchedPatientIds = Array.from(allPatientIds).filter(
            pid => !patientsWithMission.has(pid) && !patientsWithPendingOffer.has(pid)
          );

          // Determine if unmatched patient actions are allowed:
          // - Must have unmatched patients
          // - Request must be in UNDER_SELECTION or later active status
          // - EU/EXCON/ADMIN can discard/export/consolidate
          // - Requesting nation can ONLY create consolidated request when IN_TRANSIT
          const isDecisionMaker = user?.isEU || user?.isExcon || user?.isAdmin;
          const hasExistingFollowup = request.followupRequests && request.followupRequests.some(
            (fr: any) => fr.status !== 'WITHDRAWN' && fr.status !== 'CLOSED'
          );
          const canNationConsolidate =
            isRequestingNation &&
            request.status === 'IN_TRANSIT' &&
            unmatchedPatientIds.length > 0 &&
            !hasExistingFollowup &&
            !isReadOnlyInstance;
          const canManageUnmatched =
            unmatchedPatientIds.length > 0 &&
            ['UNDER_SELECTION', 'ACCEPTED', 'PLANNED', 'IN_TRANSIT'].includes(request.status) &&
            (isDecisionMaker || canNationConsolidate) &&
            !isReadOnlyInstance;

          return (
            <>
              {/* Coverage summary */}
              {((request.offers && request.offers.length > 0) || missions.length > 0) && (
                <div className="info-section">
                  <h3><Users size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Patient Coverage</h3>
                  <div className="patient-coverage-summary">
                    <div className="coverage-bar-container">
                      <div className="coverage-bar">
                        {assignedCount > 0 && (
                          <div
                            className="coverage-bar-segment assigned"
                            style={{ width: `${(assignedCount / stats.total) * 100}%` }}
                            title={`${assignedCount} assigned (in transports)`}
                          />
                        )}
                        {pendingOnlyCount > 0 && (
                          <div
                            className="coverage-bar-segment pending"
                            style={{ width: `${(pendingOnlyCount / stats.total) * 100}%` }}
                            title={`${pendingOnlyCount} with pending offers`}
                          />
                        )}
                      </div>
                      <span className={`coverage-percentage ${percentage === 100 ? 'complete' : percentage >= 50 ? 'partial' : 'low'}`}>
                        {coveredCount}/{stats.total} ({percentage}%)
                      </span>
                    </div>
                    <div className="coverage-legend">
                      {assignedCount > 0 && (
                        <span className="legend-item">
                          <span className="legend-dot assigned" />
                          {assignedCount} assigned (in transports)
                        </span>
                      )}
                      {pendingOnlyCount > 0 && (
                        <span className="legend-item">
                          <span className="legend-dot pending" />
                          {pendingOnlyCount} with pending offers
                        </span>
                      )}
                      {noOffersCount > 0 && (
                        <span className="legend-item">
                          <span className="legend-dot unmatched" />
                          {noOffersCount} with no offers
                        </span>
                      )}
                    </div>

                    {/* Unmatched patient actions — EU/EXCON or requesting nation (IN_TRANSIT) */}
                    {canManageUnmatched && (
                      <div className="unmatched-actions">
                        <div className="unmatched-actions-header">
                          <AlertTriangle size={14} />
                          <strong>{unmatchedPatientIds.length} unassigned patient{unmatchedPatientIds.length !== 1 ? 's' : ''}</strong>
                          <span className="unmatched-hint">
                            {canNationConsolidate && !isDecisionMaker
                              ? '— These patients have no assignment. You can create a consolidated request.'
                              : '— Choose an action:'}
                          </span>
                        </div>
                        <div className="unmatched-actions-buttons">
                          {/* Discard / Export only for EU/EXCON/ADMIN */}
                          {isDecisionMaker && (
                            <>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => { setUnmatchedAction('discard'); setShowUnmatchedConfirm(true); }}
                              >
                                <XCircle size={14} />
                                Discard
                              </button>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={async () => {
                                  try {
                                    const response = await requestsApi.exportUnmatchedPatientsExcel(request.id, unmatchedPatientIds);
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `unmatched-patients-${request.requestId}.xlsx`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                    setUnmatchedAction('export-discard');
                                    setShowUnmatchedConfirm(true);
                                  } catch (err: any) {
                                    setError('Failed to export patients');
                                  }
                                }}
                              >
                                <Download size={14} />
                                Export &amp; Discard
                              </button>
                            </>
                          )}
                          {/* Create Consolidated Request — EU/EXCON/ADMIN or requesting nation (IN_TRANSIT) */}
                          {!hasExistingFollowup ? (
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => { setUnmatchedAction('followup'); setShowFollowupConfirm(true); }}
                              disabled={creatingFollowup}
                            >
                              <Copy size={14} />
                              {creatingFollowup ? 'Creating...' : 'Create Consolidated Request'}
                            </button>
                          ) : (
                            <span className="followup-exists-hint">
                              <GitBranch size={12} /> Consolidated request already exists
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Discard / Export+Discard confirmation */}
                    <ConfirmDialog
                      isOpen={showUnmatchedConfirm}
                      title={unmatchedAction === 'discard' ? 'Discard Unmatched Patients' : 'Discard Exported Patients'}
                      message={`This will permanently exclude ${unmatchedPatientIds.length} patient(s) from this request. They will no longer appear in this request's status.`}
                      hint={unmatchedAction === 'export-discard' ? 'The Excel file has been downloaded.' : 'This action cannot be undone.'}
                      confirmLabel="Discard Patients"
                      cancelLabel="Cancel"
                      onConfirm={async () => {
                        try {
                          await requestsApi.excludePatients(request.id, unmatchedPatientIds);
                          setSuccess(`${unmatchedPatientIds.length} patient(s) excluded from request`);
                          setShowUnmatchedConfirm(false);
                          await loadRequest();
                          setTimeout(() => setSuccess(''), 5000);
                        } catch (err: any) {
                          setError(err.response?.data?.error || 'Failed to exclude patients');
                        }
                      }}
                      onCancel={() => setShowUnmatchedConfirm(false)}
                      confirmVariant="danger"
                    />

                    {/* Consolidated request creation confirmation */}
                    <ConfirmDialog
                      isOpen={showFollowupConfirm}
                      title="Create Consolidated Request"
                      message={`This will create a new DRAFT request containing ${unmatchedPatientIds.length} patient(s) that currently have no offers.`}
                      hint={`The new request will be linked to ${request.requestId}. The nation can edit, add patients, and submit it. Other nations will be able to make offers.`}
                      confirmLabel={creatingFollowup ? 'Creating...' : 'Create Consolidated'}
                      cancelLabel="Cancel"
                      onConfirm={() => handleCreateFollowup(unmatchedPatientIds)}
                      onCancel={() => setShowFollowupConfirm(false)}
                      confirmVariant="primary"
                    />
                  </div>
                </div>
              )}

              {/* Patient statistics grid */}
              <div className="info-section">
                <div className="patient-statistics-grid">
                  <div className="stat-card">
                    <div className="stat-label">Status</div>
                    <div className="stat-values">
                      <span>Civil: <strong>{stats.byStatus.CIVIL}</strong></span>
                      <span>Military: <strong>{stats.byStatus.MILITARY}</strong></span>
                      {stats.byStatus.TBA > 0 && <span style={{ color: '#6c757d' }}>TBA: <strong>{stats.byStatus.TBA}</strong></span>}
                      <span>Companions: <strong>{stats.companions}</strong></span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Priority</div>
                    <div className="stat-values">
                      <span className="priority-p1">P1: <strong>{stats.byPriority.P1}</strong></span>
                      <span className="priority-p2">P2: <strong>{stats.byPriority.P2}</strong></span>
                      <span className="priority-p3">P3: <strong>{stats.byPriority.P3}</strong></span>
                      {stats.byPriority.TBA > 0 && <span style={{ color: '#6c757d' }}>TBA: <strong>{stats.byPriority.TBA}</strong></span>}
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Transport</div>
                    <div className="stat-values">
                      <span>Seated: <strong>{stats.byTransport.SEATED}</strong></span>
                      <span>Lying: <strong>{stats.byTransport.LYING}</strong></span>
                      {stats.byTransport.TBA > 0 && <span style={{ color: '#6c757d' }}>TBA: <strong>{stats.byTransport.TBA}</strong></span>}
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Dependency</div>
                    <div className="stat-values">
                      <span>Low: <strong>{stats.byDependency.LOW}</strong></span>
                      <span>Med: <strong>{stats.byDependency.MEDIUM}</strong></span>
                      <span>High: <strong>{stats.byDependency.HIGH}</strong></span>
                      {stats.byDependency.TBA > 0 && <span style={{ color: '#6c757d' }}>TBA: <strong>{stats.byDependency.TBA}</strong></span>}
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

            {request.excelFile && (
              <div className="info-section">
                <label>Attached File</label>
                <a
                  href={`${process.env.REACT_APP_API_URL || ''}/api/requests/${request.id}/download`}
                  className="btn btn-outline btn-sm"
                  download
                >
                  <Download size={14} /> Download Excel File
                </a>
              </div>
            )}
          </div>

          {/* Sidebar with Route Map - only for MEDEVAC with exitPoint */}
          {request.category === 'MEDEVAC' && request.exitPoint && (
            <div className="info-sidebar">
              <MEDEVACRouteMap
                creatorNation={request.creator.nation}
                exitPoint={request.exitPoint}
                missions={missions}
              />
            </div>
          )}
        </div>
      </div>

      {/* Transport Section - in Overview tab */}
      {['ACCEPTED', 'PLANNED', 'IN_TRANSIT', 'COMPLETED'].includes(request.status) &&
       (user?.isPlanner || user?.isEU || user?.isAdmin || user?.isExcon || user?.isHub) && (
        <div className="card transport-section">
          <div className="section-header">
            <h2>{t('transportPlanning.title')}</h2>
          </div>

          {loadingTransport ? (
            <div className="loading">{t('common.loading')}</div>
          ) : transport ? (
            <div className="transport-details">
              <div className="transport-info">
                <div className="info-row">
                  <strong>Transport Hub:</strong>
                  <span>{transport.startPoint?.name} ({(transport.startPoint as any)?.types?.join(', ')})</span>
                </div>
                <div className="info-row">
                  <strong>Transport Asset:</strong>
                  <span>
                    {transport.asset?.name} ({transport.asset?.type})
                    {transport.asset && (
                      <span className="capacity-info">
                        {' '}
                        - Capacity: {transport.asset.capacitySeated} seated, {transport.asset.capacityLying} lying, {transport.asset.capacityIntensive} intensive
                      </span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <strong>Destination Medical Hub:</strong>
                  <span>{transport.hub?.name} ({transport.hub?.type})</span>
                </div>
                {transport.departureTime && (
                  <div className="info-row">
                    <strong>Planned Departure:</strong>
                    <span><UTCTime date={transport.departureTime} /></span>
                  </div>
                )}
                {transport.arrivalTime && (
                  <div className="info-row">
                    <strong>Planned Arrival:</strong>
                    <span><UTCTime date={transport.arrivalTime} /></span>
                  </div>
                )}
                {transport.notes && (
                  <div className="info-row">
                    <strong>Notes:</strong>
                    <span>{transport.notes}</span>
                  </div>
                )}
                <div className="info-row">
                  <strong>Planned By:</strong>
                  <span>{transport.planner?.nation} - {transport.planner?.email}</span>
                </div>
              </div>

              <div className="transport-actions">
                {!isReadOnlyInstance && request.status === 'PLANNED' && (user?.isPlanner || user?.isAdmin || user?.isExcon) && (
                  <button onClick={handleStartTransport} className="btn btn-primary">
                    <Play size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    Start Transport
                  </button>
                )}
                {!isReadOnlyInstance && request.status === 'IN_TRANSIT' && (user?.isHub || user?.isAdmin || user?.isExcon) && (
                  <button onClick={handleCompleteTransport} className="btn btn-success">
                    <CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    Complete Transport
                  </button>
                )}
              </div>

              {/* Timeline for IN_TRANSIT and COMPLETED */}
              {['IN_TRANSIT', 'COMPLETED'].includes(request.status) && (
                <TransportTimeline transport={transport} currentStatus={request.status} />
              )}
            </div>
          ) : request.status === 'ACCEPTED' && (user?.isPlanner || user?.isAdmin || user?.isExcon) ? (
            <div className="no-transport">
              <p>No transport plan created yet.</p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                {!isReadOnlyInstance && (
                  <button
                    onClick={() => navigate('/transport-planning')}
                    className="btn btn-primary"
                  >
                    Create Transport Plan
                  </button>
                )}
                {!isReadOnlyInstance && (
                  <button
                    onClick={() => navigate(`/transport-booking/${request.id}`)}
                    className="btn btn-secondary"
                  >
                    Book on Schedule
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="no-transport">
              <p>No transport plan available.</p>
            </div>
          )}
        </div>
      )}

      </>
      )}

      {/* ===== PATIENTS TAB ===== */}
      {activeTab === 'patients' && (
      <>
      {request.patients && request.patients.length > 0 ? (
        <div className="card patients-tab-section">
          <div className="section-header">
            <h2>{t('requests.patients')} ({request.patients.length})</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {request.category === 'MEDEVAC' && (
                <button
                  onClick={() => setShowPatientListModal(true)}
                  className="btn btn-outline btn-sm"
                >
                  <Maximize2 size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                  Open Full Patient List
                </button>
              )}
              <button
                className="btn btn-export"
                onClick={() => {
                  if (!request.patients || request.patients.length === 0) return;
                  const isMedevac = request.category === 'MEDEVAC';
                  const headers = [
                    'Patient ID', 'Initials', 'Gender', 'Age', 'Civil/Military Status',
                    'Primary Diagnosis',
                    ...(isMedevac ? ['Transport Mode (L/S)', 'Dependency Level', 'Medical Priority'] : []),
                    ...(canSeeAssignments && hasAcceptedOffers ? ['Treatment Nation', 'Treatment Hub', 'Transport Nation'] : []),
                  ];
                  const rows = filteredPatients.map((p: Patient) => {
                    const assignment = patientAssignments.get(p.id);
                    return [
                      p.patientId || '',
                      p.patientInitials || '',
                      p.patientGender || '',
                      p.patientAge ?? '',
                      p.patientStatus || '',
                      p.medicalPrimaryConditionDiagnosis || '',
                      ...(isMedevac ? [p.medicalTransport || '', p.medicalDependency || '', p.medicalPriority || ''] : []),
                      ...(canSeeAssignments && hasAcceptedOffers ? [
                        assignment?.treatment?.nation || '',
                        assignment?.treatment?.hubName || '',
                        assignment?.transport?.nation || '',
                      ] : []),
                    ];
                  });
                  exportAoaToExcel(
                    headers, rows,
                    `ARCOS_Patients_${request.requestId}`,
                    'Patients',
                    {
                      title: `Patient List — Request ${request.requestId}`,
                      subtitle: `${rows.length} patient(s) | ${request.title}`,
                    }
                  );
                }}
              >
                <FileSpreadsheet size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Export Excel
              </button>
            </div>
          </div>
          {canSeeAssignments && hasAcceptedOffers && patientAssignments.size > 0 && (() => {
            const total = request.patients?.length || 0;
            const treatmentAssigned = Array.from(patientAssignments.values()).filter(a => a.treatment !== null).length;
            const transportAssigned = Array.from(patientAssignments.values()).filter(a => a.transport !== null).length;
            const fullyAssigned = Array.from(patientAssignments.values()).filter(a => a.treatment !== null && a.transport !== null).length;
            return (
              <div className="patient-assignment-summary">
                <div className="patient-assignment-summary-item">
                  <HeartPulse size={14} />
                  <span>Treatment: <strong>{treatmentAssigned}</strong>/{total}</span>
                </div>
                <div className="patient-assignment-summary-item">
                  <Truck size={14} />
                  <span>Transport: <strong>{transportAssigned}</strong>/{total}</span>
                </div>
                <div className="patient-assignment-summary-item">
                  <CheckCircle2 size={14} />
                  <span>Fully covered: <strong>{fullyAssigned}</strong>/{total}</span>
                </div>
                {fullyAssigned < total && (
                  <div className="patient-assignment-summary-item patient-assignment-summary-warning">
                    <AlertTriangle size={14} />
                    <span>{total - fullyAssigned} patient{total - fullyAssigned !== 1 ? 's' : ''} still need coverage</span>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="patient-filters">
            <input
              type="text"
              placeholder={t('common.search') + '...'}
              className="filter-search"
              value={patientSearch}
              onChange={e => setPatientSearch(e.target.value)}
            />
            <select value={patientPriorityFilter} onChange={e => setPatientPriorityFilter(e.target.value)}>
              <option value="">{t('common.all')} {t('common.priority')}</option>
              <option value="P1">P1</option>
              <option value="P2">P2</option>
              <option value="P3">P3</option>
            </select>
            <select value={patientTransportFilter} onChange={e => setPatientTransportFilter(e.target.value)}>
              <option value="">{t('common.all')} {t('offers.transport')}</option>
              <option value="SEATED">{t('patients.seated')}</option>
              <option value="LYING">{t('patients.lying')}</option>
            </select>
            <select value={patientSortBy} onChange={e => setPatientSortBy(e.target.value)}>
              <option value="">{t('common.sortBy')}</option>
              <option value="name">{t('common.name')}</option>
              <option value="priority">{t('common.priority')}</option>
              <option value="age">{t('patients.age')}</option>
              <option value="gender">{t('patients.gender')}</option>
            </select>
          </div>
          {filteredPatients.length === 0 ? (
            <p className="no-items">{t('common.noResults')}</p>
          ) : (
          <div className="patients-table-wrapper">
            <table className="patients-table">
              <thead>
                <tr>
                  <th>{t('patients.patientId')}</th>
                  <th>Initials</th>
                  <th>{t('patients.gender')}</th>
                  <th>{t('patients.age')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('patients.diagnosis')}</th>
                  {request.category === 'MEDEVAC' && <th>L/S</th>}
                  {request.category === 'MEDEVAC' && <th>Dep.</th>}
                  {request.category === 'MEDEVAC' && <th>{t('common.priority')}</th>}
                  {canSeeAssignments && hasAcceptedOffers && <th><HeartPulse size={14} style={{ verticalAlign: 'middle', marginRight: '2px' }} />Treatment</th>}
                  {canSeeAssignments && hasAcceptedOffers && <th><Truck size={14} style={{ verticalAlign: 'middle', marginRight: '2px' }} />Transport</th>}
                </tr>
              </thead>
              <tbody>
                {filteredPatients.map((patient: any) => {
                  const assignment = patientAssignments.get(patient.id);
                  return (
                  <tr key={patient.id} className="patient-row-clickable" onClick={() => { setSelectedPatient(patient); setShowPatientModal(true); }}>
                    <td className="mono">{patient.patientId || '-'}</td>
                    <td>{patient.patientInitials || '-'}</td>
                    <td>{patient.patientGender || '-'}</td>
                    <td>{patient.patientAge ?? '-'}</td>
                    <td>
                      {patient.patientStatus ? (
                        <span className={`status-circle status-${patient.patientStatus.toLowerCase()}`}>
                          {patient.patientStatus === 'CIVIL' ? 'C' : patient.patientStatus === 'MILITARY' ? 'M' : '?'}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="diagnosis-cell-mini" title={patient.medicalPrimaryConditionDiagnosis || ''}>
                      {patient.medicalPrimaryConditionDiagnosis || '-'}
                    </td>
                    {request.category === 'MEDEVAC' && (
                      <td>
                        {patient.medicalTransport === 'LYING' && (
                          <img src="/icons/patient-liegend.webp" alt="Lying" width={18} height={18} title="Lying" />
                        )}
                        {patient.medicalTransport === 'SEATED' && (
                          <img src="/icons/patient-sitzend.webp" alt="Seated" width={18} height={18} title="Seated" />
                        )}
                        {patient.medicalTransport === 'TBA' && <span title="TBA">?</span>}
                        {!patient.medicalTransport && '-'}
                      </td>
                    )}
                    {request.category === 'MEDEVAC' && (
                      <td>
                        {patient.medicalDependency ? (
                          <span className={`badge-dependency badge-dependency-${patient.medicalDependency.toLowerCase()}`}>
                            {patient.medicalDependency}
                          </span>
                        ) : '-'}
                      </td>
                    )}
                    {request.category === 'MEDEVAC' && (
                      <td>
                        {patient.medicalPriority ? (
                          <span className={`badge badge-priority-${patient.medicalPriority.toLowerCase()}`}>
                            {patient.medicalPriority}
                          </span>
                        ) : '-'}
                      </td>
                    )}
                    {canSeeAssignments && hasAcceptedOffers && (
                      <td>
                        {assignment?.treatment ? (
                          <span className="patient-assignment-badge patient-assignment-assigned" title={`Treatment by ${assignment.treatment.nation}${assignment.treatment.hubName ? ` at ${assignment.treatment.hubName}` : ''}`}>
                            {assignment.treatment.flagImage && (
                              <img src={assignment.treatment.flagImage} alt={assignment.treatment.nation} className="patient-assignment-flag" />
                            )}
                            <span className="patient-assignment-text">
                              {assignment.treatment.hubName || assignment.treatment.nation}
                            </span>
                          </span>
                        ) : (
                          <span className="patient-assignment-badge patient-assignment-unassigned" title="No treatment assigned">
                            <CircleDot size={12} />
                            <span className="patient-assignment-text">Unassigned</span>
                          </span>
                        )}
                      </td>
                    )}
                    {canSeeAssignments && hasAcceptedOffers && (
                      <td>
                        {assignment?.transport ? (
                          <span className="patient-assignment-badge patient-assignment-assigned" title={`Transport by ${assignment.transport.nation}`}>
                            {assignment.transport.flagImage && (
                              <img src={assignment.transport.flagImage} alt={assignment.transport.nation} className="patient-assignment-flag" />
                            )}
                            <span className="patient-assignment-text">{assignment.transport.nation}</span>
                          </span>
                        ) : (
                          <span className="patient-assignment-badge patient-assignment-unassigned" title="No transport assigned">
                            <CircleDot size={12} />
                            <span className="patient-assignment-text">Unassigned</span>
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ) : (
        <div className="card patients-tab-section">
          <p className="no-items">{t('patients.noPatients')}</p>
        </div>
      )}

      {/* Missions in Patients Tab */}
      {missions.length > 0 && (
        <div className="card missions-section">
          <div className="section-header collapsible-header" onClick={() => setShowMissions(!showMissions)}>
            <h2>{t('missions.title')} ({missions.length})</h2>
            <button className="collapse-toggle" type="button">
              {showMissions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          </div>
          {showMissions && (
            <div className="missions-list">
              {missions.map(mission => (
                <div key={mission.id} className="mission-card">
                  <div className="mission-card-header">
                    <div className="mission-card-left">
                      {mission.user?.flagImage && (
                        <img src={mission.user.flagImage} alt={mission.nation} className="mission-card-flag" />
                      )}
                      <div>
                        <span className="mission-card-id">{mission.missionId}</span>
                        <span className="mission-card-nation">{mission.nation}</span>
                      </div>
                    </div>
                    <div className="mission-card-right">
                      <span className={`badge ${mission.status === 'DRAFT' ? 'badge-draft' : mission.status === 'ACTIVE' ? 'badge-published' : mission.status === 'IN_TRANSIT' ? 'badge-transit' : mission.status === 'COMPLETED' ? 'badge-completed' : 'badge-closed'}`}>
                        {mission.status === 'DRAFT' ? 'Draft' : mission.status === 'IN_TRANSIT' ? 'In Transit' : mission.status.charAt(0) + mission.status.slice(1).toLowerCase()}
                      </span>
                      <span className="mission-card-patients-count">
                        {mission.patients?.length || 0} patient{(mission.patients?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  {mission.patients && mission.patients.length > 0 && (() => {
                    const isExpanded = expandedMissions.has(mission.id);
                    const visiblePatients = isExpanded ? mission.patients : mission.patients.slice(0, 5);
                    const hasMore = mission.patients.length > 5;
                    return (
                      <div className="mission-card-patients">
                        <table className="mission-patients-mini-table">
                          <thead>
                            <tr>
                              <th>{t('patients.patientId')}</th>
                              <th>Initials</th>
                              <th>{t('patients.age')}</th>
                              <th>{t('common.status')}</th>
                              <th>{t('patients.diagnosis')}</th>
                              <th>L/S</th>
                              <th>Dep.</th>
                              <th>{t('common.priority')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visiblePatients.map(mp => (
                              <tr key={mp.id}>
                                <td className="mono">{mp.patient.patientId || '-'}</td>
                                <td>{mp.patient.patientInitials}</td>
                                <td>{mp.patient.patientAge ?? '-'}</td>
                                <td>
                                  {mp.patient.patientStatus ? (
                                    <span className={`status-circle status-${mp.patient.patientStatus.toLowerCase()}`}>
                                      {mp.patient.patientStatus === 'CIVIL' ? 'C' : mp.patient.patientStatus === 'MILITARY' ? 'M' : '?'}
                                    </span>
                                  ) : '-'}
                                </td>
                                <td className="diagnosis-cell-mini" title={mp.patient.medicalPrimaryConditionDiagnosis || ''}>
                                  {mp.patient.medicalPrimaryConditionDiagnosis || '-'}
                                </td>
                                <td>
                                  {mp.patient.medicalTransport === 'LYING' && (
                                    <img src="/icons/patient-liegend.webp" alt="Lying" width={18} height={18} title="Lying" />
                                  )}
                                  {mp.patient.medicalTransport === 'SEATED' && (
                                    <img src="/icons/patient-sitzend.webp" alt="Seated" width={18} height={18} title="Seated" />
                                  )}
                                  {mp.patient.medicalTransport === 'TBA' && <span title="TBA">?</span>}
                                  {!mp.patient.medicalTransport && '-'}
                                </td>
                                <td>
                                  {mp.patient.medicalDependency ? (
                                    <span className={`badge-dependency badge-dependency-${mp.patient.medicalDependency.toLowerCase()}`}>
                                      {mp.patient.medicalDependency}
                                    </span>
                                  ) : '-'}
                                </td>
                                <td>
                                  {mp.patient.medicalPriority ? (
                                    <span className={`badge badge-priority-${mp.patient.medicalPriority.toLowerCase()}`}>
                                      {mp.patient.medicalPriority}
                                    </span>
                                  ) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {hasMore && (
                          <button
                            className="btn-show-more"
                            onClick={() => {
                              setExpandedMissions(prev => {
                                const next = new Set(prev);
                                if (next.has(mission.id)) {
                                  next.delete(mission.id);
                                } else {
                                  next.add(mission.id);
                                }
                                return next;
                              });
                            }}
                          >
                            {isExpanded ? 'Show less' : `Show all ${mission.patients.length} patients`}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}

              {/* Export Button */}
              <button
                className="btn btn-export"
                onClick={() => {
                  const headers = [
                    'Patient ID', 'Initials', 'Age', 'Civil/Military Status',
                    'Primary Diagnosis', 'Transport Mode (L/S)', 'Dependency Level',
                    'Medical Priority', 'Mission ID', 'Treatment Nation',
                    'Transport Nation', 'Mission Status',
                    'UCPM Transit Hub', 'UCPM Exit Hub',
                  ];
                  const dataRows: (string | number)[][] = [];
                  missions.forEach(m => {
                    const transitHub = (m as any).request?.exitPoint?.name || '';
                    const exitHub = (m as any).offer?.receivingStartPoint?.name || '';
                    (m.patients || []).forEach((mp: any) => {
                      const treatmentNation = mp.assignment?.treatmentOffer?.user?.nation || '';
                      dataRows.push([
                        mp.patient.patientId || '',
                        mp.patient.patientInitials || '',
                        mp.patient.patientAge ?? '',
                        mp.patient.patientStatus || '',
                        mp.patient.medicalPrimaryConditionDiagnosis || '',
                        mp.patient.medicalTransport || '',
                        mp.patient.medicalDependency || '',
                        mp.patient.medicalPriority || '',
                        m.missionId,
                        treatmentNation,
                        m.nation,
                        m.status,
                        transitHub,
                        exitHub,
                      ]);
                    });
                  });
                  exportAoaToExcel(
                    headers, dataRows,
                    `ARCOS_Transport-Mapping_${request.requestId}`,
                    'Transport Mapping',
                    {
                      title: `Patient-Transport Mapping — Request ${request.requestId}`,
                      subtitle: `${dataRows.length} patient(s) across ${missions.length} mission(s)`,
                    }
                  );
                }}
                style={{ marginTop: '12px' }}
              >
                <FileSpreadsheet size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Export Patient-Transport Mapping
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* ===== OFFERS TAB ===== */}
      {activeTab === 'offers' && (
      <>
      {(['PUBLISHED', 'UNDER_SELECTION', 'ACCEPTED', 'PLANNED', 'CLOSED', 'IN_TRANSIT', 'COMPLETED'].includes(request.status)) ? (
        <div className="card offers-section">
          <div className="section-header">
            <h2>{t('requests.offers')} ({request.offers?.length || 0})</h2>
          </div>

          <div className="offers-list">
            {request.offers && request.offers.length > 0 ? (
              request.category === 'MEDEVAC' ? (
                request.offers.map((offer) => (
                  <OfferItem
                    key={offer.id}
                    offer={offer}
                    requestId={request.id}
                    requestStatus={request.status}
                    requestCategory={request.category}
                    requestCreatorId={request.creatorId}
                    totalPatients={request.patients?.length || 0}
                    canAccept={!!(canAcceptOffer && request.status === 'PUBLISHED')}
                    canWithdraw={offer.user.id === user?.id && request.status === 'PUBLISHED'}
                    canEdit={offer.user.id === user?.id && request.status === 'PUBLISHED'}
                    canViewDetails={!!(offer.user.id === user?.id || request.creatorId === user?.id || user?.isEU || user?.isAdmin || user?.isExcon || offer.transportProviderNationId === user?.nation)}
                    onAccept={handleAcceptOffer}
                    onWithdraw={handleWithdrawOffer}
                    onEdit={handleEditOffer}
                  />
                ))
              ) : (
                request.offers.map((offer) => (
                  <SimpleOfferItem
                    key={offer.id}
                    offer={offer}
                    canEdit={offer.user.id === user?.id && request.status === 'PUBLISHED'}
                    canWithdraw={offer.user.id === user?.id && request.status === 'PUBLISHED'}
                    canAccept={!!(canAcceptOffer && request.status === 'PUBLISHED')}
                    onEdit={handleEditTextOffer}
                    onWithdraw={handleWithdrawTextOffer}
                    onAccept={handleAcceptOffer}
                  />
                ))
              )
            ) : (
              <p className="no-items">{t('requests.noOffers')}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="card offers-section">
          <p className="no-items">{t('offers.noOffers')}</p>
        </div>
      )}
      </>
      )}

      {/* ===== COMMENTS TAB ===== */}
      {activeTab === 'comments' && (
        <div className="card comments-section">
          <h2>{t('common.comments')} ({request.comments?.length || 0})</h2>

          {canComment && (
            <form onSubmit={handleCommentSubmit} className="comment-form">
              <div className="form-group">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={t('requests.writeComment')}
                  required
                  rows={3}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={submittingComment}>
                {submittingComment ? t('requests.posting') : t('requests.postComment')}
              </button>
            </form>
          )}

          <div className="comments-list">
            {request.comments && request.comments.length > 0 ? (() => {
              // Sort newest first
              const sorted = [...request.comments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
              // Find the first EU comment to pin (check for EU nation or EU Review Comment prefix)
              const pinnedComment = sorted.find(c =>
                c.user.nation === 'EU' || c.content.startsWith('**EU Review Comment:**')
              );
              const otherComments = pinnedComment
                ? sorted.filter(c => c.id !== pinnedComment.id)
                : sorted;

              return (
                <>
                  {pinnedComment && (() => {
                    const pinnedNation = getNationByName(pinnedComment.user.nation);
                    const pinnedRole = getCommentRoleLabel(pinnedComment.user, t);
                    return (
                      <div className="comment-item other-comment">
                        <div className="comment-bubble comment-pinned">
                          <div className="comment-header">
                            <div className="comment-author">
                              {pinnedNation?.flagImage && (
                                <img
                                  src={pinnedNation.flagImage}
                                  alt={pinnedComment.user.nation}
                                  className="comment-flag"
                                />
                              )}
                              <strong>{pinnedComment.user.nation}</strong>
                              <span className="comment-role-badge">{pinnedRole}</span>
                              <span className="pinned-badge"><Pin size={12} /> Pinned</span>
                            </div>
                            <span className="comment-date">
                              <UTCTime date={pinnedComment.createdAt} />
                            </span>
                          </div>
                          <div className="comment-content">{pinnedComment.content}</div>
                        </div>
                      </div>
                    );
                  })()}
                  {otherComments.map((comment) => {
                    const isOwnComment = comment.user.id === user?.id;
                    const commentNation = getNationByName(comment.user.nation);
                    const commentRole = getCommentRoleLabel(comment.user, t);
                    const authorColorClass = !isOwnComment ? `comment-author-${getCommentAuthorColorIndex(comment.user.id)}` : '';
                    return (
                      <div key={comment.id} className={`comment-item ${isOwnComment ? 'own-comment' : 'other-comment'}`}>
                        <div className={`comment-bubble ${authorColorClass}`}>
                          <div className="comment-header">
                            <div className="comment-author">
                              {commentNation?.flagImage && (
                                <img
                                  src={commentNation.flagImage}
                                  alt={comment.user.nation}
                                  className="comment-flag"
                                />
                              )}
                              <strong>{comment.user.nation}</strong>
                              <span className="comment-role-badge">{commentRole}</span>
                            </div>
                            <span className="comment-date">
                              <UTCTime date={comment.createdAt} />
                            </span>
                          </div>
                          <div className="comment-content">{comment.content}</div>
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })() : (
              <p className="no-items">{t('requests.noComments')}</p>
            )}
          </div>
        </div>
      )}

      {/* ===== HISTORY TAB ===== */}
      {activeTab === 'history' && (
        <div className="card audit-log-section">
          <h2>{t('requests.history')} ({auditLogs.length})</h2>
          {loadingAuditLogs ? (
            <p>{t('common.loading')}</p>
          ) : auditLogs.length > 0 ? (
            <div className="history-timeline">
              {auditLogs.map((log, idx) => (
                <div key={log.id || idx} className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <span className="timeline-action">{formatAction(log.action)}</span>
                      <span className="timeline-date">{new Date(log.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC</span>
                    </div>
                    <div className="timeline-user">
                      {log.userNation && <img src={getNationFlag(log.userNation)} alt={log.userNation} className="timeline-flag" />}
                      <span className="timeline-user-name">{log.userDisplayName || log.userNation || 'System'}</span>
                      {getTimelineRoleLabel(log) && (
                        <span className="timeline-role-badge">{getTimelineRoleLabel(log)}</span>
                      )}
                    </div>
                    {log.userEmail && (
                      <div className="timeline-email">{log.userEmail}</div>
                    )}
                    {log.details && (() => {
                      try {
                        const details = JSON.parse(log.details);
                        if (details.changes || details.comment) {
                          return <div className="timeline-details">{details.comment || JSON.stringify(details.changes)}</div>;
                        }
                      } catch {}
                      return null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          ) : request ? (
            <div className="history-timeline">
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-action">Request Created</span>
                    <span className="timeline-date">{new Date(request.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC</span>
                  </div>
                  <div className="timeline-user">
                    <span>{request.creator?.nation || 'Unknown'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="no-items">{t('common.noData')}</p>
          )}
        </div>
      )}

      </TabPanel>

      {/* Publish Dialog */}
      {showPublishDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{t('requests.publish')}</h2>
            <p>This will publish the request and make it visible to all nations for offers.</p>
            <form onSubmit={handlePublish}>
              <div className="form-group">
                <label>EU Review Comment (optional)</label>
                <textarea
                  value={publishComment}
                  onChange={(e) => setPublishComment(e.target.value)}
                  placeholder="Add a review comment or instructions for nations (optional)..."
                  rows={5}
                  className="form-control"
                  style={{
                    border: '2px solid var(--color-success)',
                    background: 'var(--color-success-light)',
                    fontSize: 'var(--font-size-sm)'
                  }}
                />
                <small style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-1)', display: 'block' }}>
                  <Lightbulb size={12} /> This comment will be highlighted at the top of the comments section
                </small>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowPublishDialog(false);
                    setPublishComment('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={submittingPublish}
                >
                  {submittingPublish ? t('common.loading') : t('requests.publish')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{t('status.rejected')}</h2>
            <p>This will return the request to DRAFT status and notify the creator.</p>
            <form onSubmit={handleReject}>
              <div className="form-group">
                <label>Reason for rejection (required)</label>
                <textarea
                  value={rejectMessage}
                  onChange={(e) => setRejectMessage(e.target.value)}
                  placeholder="Please provide a detailed reason for rejection..."
                  rows={5}
                  required
                  className="form-control"
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectMessage('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-warning"
                  disabled={submittingReject}
                >
                  {submittingReject ? 'Rejecting...' : 'Reject Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Close Dialog */}
      {showCloseDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{t('requests.closeRequest')}</h2>
            <p>Are you sure you want to close this request?</p>
            <p style={{ color: 'var(--color-warning-text)', marginTop: 'var(--space-2-5)' }}>
              <AlertTriangle size={14} /> This will prevent new offers from being submitted. The request can be re-opened later if needed.
            </p>
            <form onSubmit={handleClose}>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowCloseDialog(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-warning"
                  disabled={submittingClose}
                >
                  {submittingClose ? 'Closing...' : 'Close Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Patient Details Modal */}
      {showPatientModal && selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          onClose={() => {
            setShowPatientModal(false);
            setSelectedPatient(null);
          }}
        />
      )}

      {/* Patient List Modal - Read-only view with filters */}
      {showPatientListModal && request && (
        <PatientListModal
          requestId={request.id}
          requestStatus={request.status}
          requestCreatorId={request.creatorId}
          requestCategory={request.category}
          onClose={() => {
            setShowPatientListModal(false);
          }}
        />
      )}

      {/* Edit Offer Modal */}
      {showEditOfferModal && editingOffer && request && (
        <OfferCreationModal
          requestId={request.id}
          patientIds={editingOffer.selectedPatientIds || editingOffer.offerPatients?.map((op: any) => op.patientId) || []}
          existingOffer={editingOffer}
          onClose={() => {
            setShowEditOfferModal(false);
            setEditingOffer(null);
          }}
          onSuccess={handleEditOfferSuccess}
        />
      )}

      {/* Create Offer Modal (for new offers) */}
      {showCreateOfferModal && request && (
        <OfferCreationModal
          requestId={request.id}
          patientIds={selectedPatientIdsForOffer}
          onClose={() => {
            setShowCreateOfferModal(false);
            setSelectedPatientIdsForOffer([]);
          }}
          onSuccess={handleCreateOfferSuccess}
        />
      )}

      {/* Simple Text Offer Modal (for Personnel/Material/Other) */}
      {showTextOfferModal && request && (
        <div className="modal-overlay" onClick={() => setShowTextOfferModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <SimpleOfferForm
              requestId={request.id}
              existingOffer={editingTextOffer}
              onSuccess={() => {
                setShowTextOfferModal(false);
                setEditingTextOffer(null);
                setSuccess(editingTextOffer ? 'Offer updated successfully.' : 'Offer submitted successfully.');
                loadRequest();
                loadAuditLogs();
                setTimeout(() => setSuccess(''), 3000);
              }}
              onCancel={() => {
                setShowTextOfferModal(false);
                setEditingTextOffer(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Manage Offers Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showManageOffersConfirm}
        title={t('requests.startSelection')}
        message="This will change the request status to 'Under Selection'."
        details={[
          'You can assign offers to patients and accept them',
          'You can reopen the request for offers later (if no assignments have been accepted yet)'
        ]}
        warning="Other nations will no longer be able to submit new offers or edit existing ones. This restriction applies immediately."
        hint="If you only want to view offer details, you can use the 'View Details' button on each offer instead."
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        onConfirm={async () => {
          setShowManageOffersConfirm(false);
          try {
            await requestsApi.startSelection(request!.id);
            navigate(`/requests/${request!.id}/manage-offers`);
          } catch (err: any) {
            setError(err.response?.data?.error || t('common.failedToUpdate'));
          }
        }}
        onCancel={() => setShowManageOffersConfirm(false)}
      />

      {/* Reopen for Offers Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReopenConfirm}
        title={t('requests.publish')}
        message="This will change the request status back to 'Published'."
        details={[
          'Nations can submit new offers or edit existing ones again',
          'All current draft assignments will be discarded',
          'This is only possible if no assignments have been accepted yet'
        ]}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={async () => {
          setShowReopenConfirm(false);
          try {
            await requestsApi.reopen(request!.id);
            setSuccess(t('common.success'));
            await loadRequest();
            setTimeout(() => setSuccess(''), 3000);
          } catch (err: any) {
            setError(err.response?.data?.error || t('common.failedToUpdate'));
          }
        }}
        onCancel={() => setShowReopenConfirm(false)}
      />

      {/* Send Reminder Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReminderConfirm}
        title={t('common.actions')}
        message="This will send an email reminder to all nations that have not yet submitted an offer."
        details={[
          `${request?.offers?.length || 0} offer(s) received so far`,
          'An email notification will be sent to all registered nation users',
          'Nations that have already submitted an offer will also be notified'
        ]}
        hint="Use this sparingly to avoid notification fatigue."
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        confirmVariant="primary"
        onConfirm={async () => {
          setShowReminderConfirm(false);
          await handleSendReminder();
        }}
        onCancel={() => setShowReminderConfirm(false)}
      />

      {/* Withdraw Request Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showWithdrawDialog}
        title={t('requests.withdrawRequest')}
        message="What would you like to do with this request?"
        details={[
          'Revert to Draft: The request will be moved back to draft status for editing',
          'Delete Permanently: The request and all related data will be permanently removed'
        ]}
        hint="Reverting to draft allows you to modify and resubmit. Deleting is irreversible."
        confirmLabel={t('common.delete')}
        confirmVariant="danger"
        onConfirm={handleDeleteRequest}
        secondaryLabel={t('requests.revertToDraft')}
        secondaryVariant="warning"
        onSecondary={handleRevertToDraft}
        onCancel={() => setShowWithdrawDialog(false)}
      />

      {/* Make an Offer Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showMakeOfferConfirm}
        title={t('offers.submitOffer')}
        message="You are about to make an offer for this request."
        details={[
          'Your offer will be visible to the requesting nation and EU coordinators',
          'You can edit or withdraw your offer while the request remains Published'
        ]}
        warning="Once the requesting nation starts the selection process, you will no longer be able to modify or withdraw your offer."
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        confirmVariant="primary"
        onConfirm={() => {
          setShowMakeOfferConfirm(false);
          if (makeOfferType === 'medevac') {
            navigate(`/requests/${request!.id}/offers/create`);
          } else {
            handleCreateTextOffer();
          }
        }}
        onCancel={() => setShowMakeOfferConfirm(false)}
      />

      {/* EU Direct-Assign Transport Dialog */}
      {showDirectAssign && request?.category === 'TRANSPORT' && (
        <div className="confirm-dialog-overlay" onClick={() => setShowDirectAssign(false)}>
          <div className="confirm-dialog" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h3>Assign Transport Directly</h3>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px' }}>
              Assign transport for all {request.patients?.length || 0} patient(s) directly. This skips the offer cycle.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '0.85rem' }}>
                  Asset / Transport Provider <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <input
                  type="text"
                  value={directAssignAsset}
                  onChange={(e) => setDirectAssignAsset(e.target.value)}
                  placeholder="e.g. Charter Flight EU-MED-2026-03, DHL Medical Transport..."
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <small style={{ color: '#777' }}>Free text — not limited to predefined assets</small>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '0.85rem' }}>{t('offers.departureDate')} (UTC)</label>
                <input
                  type="datetime-local"
                  value={directAssignDate}
                  onChange={(e) => setDirectAssignDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <small className="utc-hint" style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{t('common.utcStoredHint')}</small>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '0.85rem' }}>Notes</label>
                <textarea
                  value={directAssignNotes}
                  onChange={(e) => setDirectAssignNotes(e.target.value)}
                  placeholder="Additional transport details..."
                  rows={3}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn-outline" onClick={() => setShowDirectAssign(false)} disabled={directAssignSubmitting}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={directAssignSubmitting || !directAssignAsset.trim()}
                onClick={async () => {
                  setDirectAssignSubmitting(true);
                  try {
                    const patientIds = (request.patients || []).map((p: any) => p.id);
                    await requestsApi.directAssignTransport(request.id, {
                      patientIds,
                      customAssetName: directAssignAsset.trim(),
                      departureDate: directAssignDate || undefined,
                      transportNotes: directAssignNotes.trim() || undefined,
                    });
                    setShowDirectAssign(false);
                    setDirectAssignAsset('');
                    setDirectAssignDate('');
                    setDirectAssignNotes('');
                    setSuccess('Transport assigned successfully');
                    await loadRequest();
                    setTimeout(() => setSuccess(''), 5000);
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Failed to assign transport');
                  } finally {
                    setDirectAssignSubmitting(false);
                  }
                }}
              >
                {directAssignSubmitting ? 'Assigning...' : `Assign Transport (${request.patients?.length || 0})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Import Preview Modal */}
      {showExcelPreview && (
        <div className="confirm-dialog-overlay" onClick={() => { setShowExcelPreview(false); setExcelPreviewFile(null); }}>
          <div className="excel-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="excel-preview-header">
              <h3>Import Preview — {excelPreviewFile?.name}</h3>
              <button className="btn-close" onClick={() => { setShowExcelPreview(false); setExcelPreviewFile(null); }}>
                <XCircle size={20} />
              </button>
            </div>

            {/* Summary bar */}
            {previewSummary && (
              <div className="excel-preview-summary">
                <span className="stat-inline"><strong>{previewSummary.total}</strong> patients</span>
                <span className="stat-sep">|</span>
                <span className="stat-inline priority-p1">P1: <strong>{previewSummary.p1}</strong></span>
                <span className="stat-inline priority-p2">P2: <strong>{previewSummary.p2}</strong></span>
                <span className="stat-inline priority-p3">P3: <strong>{previewSummary.p3}</strong></span>
                <span className="stat-sep">|</span>
                <span className="stat-inline">Seated: <strong>{previewSummary.seated}</strong></span>
                <span className="stat-inline">Lying: <strong>{previewSummary.lying}</strong></span>
                {previewSummary.withCompanions > 0 && (
                  <>
                    <span className="stat-sep">|</span>
                    <span className="stat-inline">Companions: <strong>{previewSummary.withCompanions}</strong></span>
                  </>
                )}
                {excludedRows.size > 0 && (
                  <>
                    <span className="stat-sep">|</span>
                    <span className="stat-inline" style={{ color: 'var(--color-danger)' }}>Excluded: <strong>{excludedRows.size}</strong></span>
                  </>
                )}
              </div>
            )}

            {/* Error/warning chips */}
            {previewErrors.length > 0 && (() => {
              const skipped = previewErrors.filter(e => (e.message || e.error || '').startsWith('Skipped:'));
              const warnings = previewErrors.filter(e => !(e.message || e.error || '').startsWith('Skipped:'));
              return (
                <div className="excel-preview-errors">
                  <div className="error-summary-bar">
                    {skipped.length > 0 && (
                      <button type="button" className={`error-summary-chip chip-danger ${showPreviewSkipped ? 'active' : ''}`}
                        onClick={() => setShowPreviewSkipped(!showPreviewSkipped)}>
                        {skipped.length} Skipped
                      </button>
                    )}
                    {warnings.length > 0 && (
                      <button type="button" className={`error-summary-chip chip-warning ${showPreviewWarnings ? 'active' : ''}`}
                        onClick={() => setShowPreviewWarnings(!showPreviewWarnings)}>
                        {warnings.length} Warning{warnings.length > 1 ? 's' : ''}
                      </button>
                    )}
                    <span className="error-summary-chip chip-success">{previewPatients.length} Passed</span>
                  </div>
                  {showPreviewSkipped && skipped.length > 0 && (
                    <div className="error-details-list">
                      {skipped.map((err, idx) => (
                        <div key={`skip-${idx}`} className="error-detail-item error-detail-danger">
                          <strong>Row {err.row}:</strong> {err.message || err.error}
                        </div>
                      ))}
                    </div>
                  )}
                  {showPreviewWarnings && warnings.length > 0 && (
                    <div className="error-details-list">
                      {warnings.map((err, idx) => (
                        <div key={`warn-${idx}`} className="error-detail-item error-detail-warning">
                          <strong>Row {err.row}:</strong> {err.message || err.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Patient table */}
            {previewPatients.length > 0 && (
              <div className="excel-preview-table-wrapper">
                <table className="excel-preview-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Initials</th>
                      <th>Age</th>
                      <th>Gender</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Diagnosis</th>
                      <th>Transport</th>
                      <th>Dep.</th>
                      <th>Comp.</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewPatients.map((patient: any, idx: number) => (
                      <tr key={idx} className={excludedRows.has(idx) ? 'row-excluded' : ''}>
                        <td>{idx + 1}</td>
                        <td><strong>{patient.patientInitials || '-'}</strong></td>
                        <td>{patient.patientAge || '-'}</td>
                        <td>{patient.patientGender || '-'}</td>
                        <td>{patient.patientStatus || '-'}</td>
                        <td>
                          <span className={`badge badge-priority-${patient.medicalPriority?.toLowerCase() || 'default'}`}>
                            {patient.medicalPriority || '-'}
                          </span>
                        </td>
                        <td className="excel-preview-diagnosis">{patient.medicalPrimaryConditionDiagnosis || '-'}</td>
                        <td>{patient.medicalTransport || '-'}</td>
                        <td>{patient.medicalDependency || '-'}</td>
                        <td>{patient.patientCompanionsCount > 0 ? patient.patientCompanionsCount : '-'}</td>
                        <td>
                          <button
                            type="button"
                            className={`btn-remove-patient ${excludedRows.has(idx) ? 'btn-restore-patient' : ''}`}
                            onClick={() => handleExcludePatient(idx)}
                            title={excludedRows.has(idx) ? 'Restore patient' : 'Exclude patient'}
                          >
                            {excludedRows.has(idx) ? '+' : <XCircle size={14} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Actions */}
            <div className="excel-preview-footer">
              <button className="btn btn-secondary" onClick={() => { setShowExcelPreview(false); setExcelPreviewFile(null); }}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmExcelImport}
                disabled={importingExcel || previewPatients.length - excludedRows.size === 0}
              >
                {importingExcel ? 'Importing...' : `Import ${previewPatients.length - excludedRows.size} Patient(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default RequestDetail;
